/**
 * FloorplansFallback
 *
 * Theme-owned floorplan grid used by ApartmentSyncSection when the
 * ApartmentSync widget has no `javascript_id` configured. The shared
 * `@vibesites/blocks` floorplans block is read-only and hardcodes its CTAs
 * (to /apartments) and a "From $X/mo" price, neither of which is exposed as a
 * token — so this copy renders the same card grid but:
 *   - points every CTA at the Contact Us page ("Contact Us")
 *   - omits the price entirely
 */

import { useFloorplans } from '@vibesites/runtime/react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  VibesitesImage,
} from '@vibesites/ui-primitives';

const CONTACT_LABEL = 'Contact Us';
const CONTACT_URL = '/contact-us';

export function FloorplansFallback() {
  const floorplans = useFloorplans();

  // Empty state
  if (floorplans.length === 0) {
    return (
      <div className="py-16 text-center bg-muted" style={{ color: 'var(--vibe-color-text-muted)' }}>
        <div className="mx-auto max-w-md">
          <h3 className="mb-2 text-lg font-medium">Floor Plans</h3>
          <p className="text-sm">No floor plans are available for this property.</p>
        </div>
      </div>
    );
  }

  return (
    <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-muted">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 text-center">
          <h2
            className="text-2xl font-bold tracking-tight md:text-3xl mb-2"
            style={{ color: 'var(--vibe-color-text)', fontFamily: 'var(--vibe-font-heading)' }}
          >
            Floor Plans
          </h2>
        </div>

        <div
          className={
            floorplans.length === 2
              ? 'grid gap-6 sm:grid-cols-2 max-w-3xl mx-auto'
              : 'grid gap-6 sm:grid-cols-2 lg:grid-cols-3'
          }
        >
          {floorplans.map((plan, i) => {
            const bedrooms = plan.floorplan_beds_min ? parseInt(plan.floorplan_beds_min) : undefined;
            const bathrooms = plan.floorplan_baths_min;
            const minSqft = plan.floorplan_sqft_min;
            const maxSqft = plan.floorplan_sqft_max;

            return (
              <Card key={plan.floorplan_id || i} className="overflow-hidden group">
                {plan.floorplan_image_url && (
                  <div className="relative aspect-[4/3] overflow-hidden bg-background">
                    <VibesitesImage
                      url={plan.floorplan_image_url}
                      alt={plan.floorplan_name || 'Floor plan'}
                      fill
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      objectFit="contain"
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-lg">{plan.floorplan_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                    {bedrooms !== undefined && (
                      <span>{bedrooms === 0 ? 'Studio' : `${bedrooms} Bed`}</span>
                    )}
                    {bathrooms && <span>{bathrooms} Bath</span>}
                    {minSqft && (
                      <span>
                        {minSqft}
                        {maxSqft && maxSqft !== minSqft ? `-${maxSqft}` : ''} sq ft
                      </span>
                    )}
                  </div>
                  <Button variant="outline" className="w-full mt-4" asChild>
                    <a href={CONTACT_URL}>{CONTACT_LABEL}</a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default FloorplansFallback;
